import type { Expression } from './Face';
import { expr } from './expressions';

// Visages en accesseurs : relus à chaque usage, ils suivent les expressions re-validées dans le labo.

/** Une humeur pilote le visage, la respiration, l'amplitude des mouvements et le comportement du regard. */
export interface Mood {
  id: string;
  label: string;                 // affiché dans le HUD (français)
  face: Expression;              // poids ARKit
  breathRate: number;            // respirations / minute
  breathDepth: number;           // 0..1.5
  sway: number;                  // amplitude du balancement
  gazeContact: number;           // 0..1 : propension au contact visuel
  gazeAversion: number;          // détournements du regard / minute
  headTilt: number;              // inclinaison de tête (rad, + = vers son épaule gauche)
  headPitch: number;             // + = tête baissée
  blinkRate: number;             // clignements / minute
}

const sym = (base: string, v: number): Expression => ({ [base + 'Left']: v, [base + 'Right']: v });

export const MOODS: Record<string, Mood> = {
  sereine: {
    id: 'sereine', label: 'sereine',
    get face(): Expression { return expr('sourire', 0.35); },
    breathRate: 13, breathDepth: 1, sway: 1, gazeContact: 0.75, gazeAversion: 2.5, headTilt: 0.03, headPitch: 0.0, blinkRate: 15,
  },
  joyeuse: {
    id: 'joyeuse', label: 'joyeuse',
    get face(): Expression { return expr('sourire', 1); },
    breathRate: 16, breathDepth: 1.1, sway: 1.3, gazeContact: 0.85, gazeAversion: 2, headTilt: 0.06, headPitch: -0.02, blinkRate: 17,
  },
  timide: {
    id: 'timide', label: 'timide',
    get face(): Expression { return expr('timide', 0.8); },
    breathRate: 15, breathDepth: 0.9, sway: 0.7, gazeContact: 0.4, gazeAversion: 7, headTilt: 0.09, headPitch: 0.08, blinkRate: 22,
  },
  curieuse: {
    id: 'curieuse', label: 'curieuse',
    get face(): Expression { return { browInnerUp: 0.22, ...sym('browOuterUp', 0.28), ...sym('eyeWide', 0.14), ...expr('sourire', 0.2) }; },
    breathRate: 14, breathDepth: 1, sway: 1.1, gazeContact: 0.9, gazeAversion: 3, headTilt: 0.14, headPitch: -0.03, blinkRate: 12,
  },
  reveuse: {
    id: 'reveuse', label: 'rêveuse',
    get face(): Expression { return { ...expr('tendresse', 0.6), ...sym('eyeBlink', 0.12) }; },
    breathRate: 10, breathDepth: 1.25, sway: 0.8, gazeContact: 0.45, gazeAversion: 4, headTilt: 0.07, headPitch: 0.03, blinkRate: 10,
  },
  melancolique: {
    id: 'melancolique', label: 'mélancolique',
    get face(): Expression { return { ...expr('tristesse', 0.55), ...sym('eyeBlink', 0.1) }; },
    breathRate: 11, breathDepth: 1.2, sway: 0.6, gazeContact: 0.3, gazeAversion: 6, headTilt: 0.05, headPitch: 0.09, blinkRate: 11,
  },
    boudeuse: {
    id: 'boudeuse', label: 'boudeuse',
    get face(): Expression { return expr('boudeuse', 1); },
    breathRate: 15, breathDepth: 1.1, sway: 0.6, gazeContact: 0.35, gazeAversion: 6, headTilt: -0.05, headPitch: 0.05, blinkRate: 14,
  },
};

/** Réactions brèves (déclenchées par le toucher, l'approche, une nouvelle tenue…). */
export const REACTIONS: Record<string, { face: Expression; duration: number; headPitch?: number; headTilt?: number }> = {
  surprise: { get face(): Expression { return expr('surprise', 1); }, duration: 1.1, headPitch: -0.05 },
  rire: { get face(): Expression { return expr('rire', 1); }, duration: 1.8, headPitch: -0.07 },
  rougir: { get face(): Expression { return expr('timide', 1.05); }, duration: 2.4, headPitch: 0.1, headTilt: 0.12 },
  clin: { get face(): Expression { return { ...expr('sourire', 0.8), eyeBlinkLeft: 1, 'bu:LeftCheekUp': 1.2 }; }, duration: 0.6 },
};
